/**
 * @fileoverview Parent sign-in, sign-up and email verification (F1).
 *
 * One page, three states, because they are one journey: a parent arriving
 * from a summary email does not know whether they already have an account,
 * and making them choose a door before they can act is the most common way
 * to lose them.
 *
 * The verification step is not a nicety here — a parent's address is what
 * links them to their child, so proving control of the inbox is the whole
 * basis of the access that follows. The copy says so plainly rather than
 * treating it as a formality.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import parentApi, { getParentErrorMessage } from "@/lib/parentApi";
import { Card, Button } from "@/components/studio/primitives";
import { authLinkCls } from "@/components/auth/authUi";

const FIELD =
  "w-full rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 py-2.5 font-sans text-sm text-[var(--text-primary)]";

export function ParentLoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | signup | verify
  const [form, setForm] = useState({ email: "", password: "", first_name: "", code: "", tos: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "signup") {
        await parentApi.post("/parent/auth/signup", {
          email: form.email,
          password: form.password,
          first_name: form.first_name,
          tos_accepted: form.tos,
        });
        setMode("verify");
        toast.success("Check your email for a 6-digit code");
      } else if (mode === "verify") {
        await parentApi.post("/parent/auth/verify-email", { email: form.email, code: form.code });
        navigate("/parent");
      } else {
        await parentApi.post("/parent/auth/login", { email: form.email, password: form.password });
        navigate("/parent");
      }
    } catch (err) {
      // An unverified account is not a failed login — it is the same journey
      // one step earlier, so it moves the form rather than showing an error.
      if (err.response?.data?.detail?.code === "email_not_verified") {
        setMode("verify");
        toast.message("Check your email for a 6-digit code");
      } else {
        setError(getParentErrorMessage(err, "That didn't work"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-[440px] flex-col justify-center px-6">
      <h1 className="mb-2 font-serif text-3xl text-[var(--text-primary)]">
        {mode === "signup" ? "Create a parent account" : mode === "verify" ? "Check your email" : "Parent sign in"}
      </h1>
      <p className="mb-6 font-sans text-[14px] text-[var(--text-tertiary)]">
        {mode === "verify"
          ? "We sent a 6-digit code. Confirming your email is what connects you to your child's lessons."
          : "See your child's lesson summaries in one place, across every teacher."}
      </p>

      <Card>
        <form onSubmit={submit} className="flex flex-col gap-3">
          {mode !== "verify" ? (
            <>
              {mode === "signup" ? (
                <input
                  className={FIELD}
                  placeholder="Your first name"
                  value={form.first_name}
                  onChange={set("first_name")}
                  data-testid="parent-first-name"
                />
              ) : null}
              <input
                className={FIELD}
                type="email"
                required
                placeholder="Email"
                value={form.email}
                onChange={set("email")}
                data-testid="parent-email"
              />
              <input
                className={FIELD}
                type="password"
                required
                minLength={8}
                placeholder="Password"
                value={form.password}
                onChange={set("password")}
                data-testid="parent-password"
              />
              {mode === "signup" ? (
                <label className="flex items-start gap-2 font-sans text-[13px] text-[var(--text-secondary)]">
                  <input
                    type="checkbox"
                    checked={form.tos}
                    onChange={(e) => setForm((f) => ({ ...f, tos: e.target.checked }))}
                    data-testid="parent-tos"
                    className="mt-0.5"
                  />
                  <span>
                    I agree to the{" "}
                    <Link to="/terms" target="_blank" rel="noopener" className={authLinkCls}>
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy" target="_blank" rel="noopener" className={authLinkCls}>
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>
              ) : null}
            </>
          ) : (
            <input
              className={FIELD}
              inputMode="numeric"
              required
              placeholder="6-digit code"
              value={form.code}
              onChange={set("code")}
              data-testid="parent-code"
            />
          )}

          {error ? (
            <p className="font-sans text-[13px] text-[var(--state-danger)]" data-testid="parent-error">
              {error}
            </p>
          ) : null}

          <Button type="submit" disabled={busy} data-testid="parent-submit">
            {mode === "signup" ? "Create account" : mode === "verify" ? "Confirm" : "Sign in"}
          </Button>
        </form>
      </Card>

      {mode !== "verify" ? (
        <button
          type="button"
          onClick={() => {
            setMode(mode === "signup" ? "login" : "signup");
            setError(null);
          }}
          data-testid="parent-mode-switch"
          className="mt-4 font-sans text-[13px] text-[var(--text-tertiary)] underline"
        >
          {mode === "signup" ? "I already have an account" : "Create a parent account"}
        </button>
      ) : null}
    </div>
  );
}

export default ParentLoginPage;
